import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Row, Col, Checkbox, Button, Space, DatePicker } from 'antd';
import { PlusOutlined, FilterOutlined, SortAscendingOutlined, SortDescendingOutlined, FolderOpenOutlined, ReloadOutlined } from '@ant-design/icons';
import './Filter.less'; 
import AddEdit from './AddEdit';
import { Project, ProjectPageModel, SortOptions, projectPageModel } from '../model';
import { User } from '@/common/model'; 
import { IconFont } from '@/common';
import moment from 'moment'; 
import { ctrl } from '../controller';
import { DateUtil } from '@/utils/DateUtil';

interface FilterProps {
  model: ProjectPageModel;
  currentUser: User;
}

const Filter: React.FC<FilterProps> = observer(({ model, currentUser }) => {

  const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);


  const handleAdd = () => {
    setSelectedProject(undefined);
    setIsModalVisible(true);
  }; 

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };
 

  const handleIncompleteChange = () => {
    model.filterOptions.onlyIncomplete = !model.filterOptions.onlyIncomplete;
    if (model.filterOptions.onlyIncomplete) {
      model.filterOptions.status = [];
    }
    model.applyFilters();
  };

  const handleFilterChange = (status: number) => {
    model.filterOptions.onlyIncomplete = false;
    if (model.filterOptions.status.includes(status)) {
      model.filterOptions.status = model.filterOptions.status.filter((s) => s !== status);
    } else {
      model.filterOptions.status.push(status);
    }
    model.applyFilters(); // 更新过滤器
  };

  const selectTimeFilter = (mode: 'all' | 'month' | 'unscheduled', month?: Date) => {
    model.monthFilterMode = mode;
    if (mode === 'month') {
      model.currentMonth = month ?? model.currentMonth ?? new Date();
    } else {
      model.currentMonth = undefined;
    }
    void model.reloadProjects();
  };

  const timeFilterBtnClass = (mode: 'all' | 'month' | 'unscheduled') =>
    model.monthFilterMode === mode ? 'time-filter-active' : '';

  const handleOwnChange = () => {
    model.filterOptions.submitterId = model.filterOptions.submitterId ? null : currentUser.id;
    model.applyFilters(); // 更新过滤器
  };

  const handleSortChange = (field: keyof SortOptions) => { 

      model.sortOptions.order = model.sortOptions.order === 'asc' ? 'desc' : 'asc';

      Object.keys(model.sortOptions).forEach(key => { 
        if( String(key) !== 'order'){
            model.sortOptions[key] = null; 
        }
      });

      if(field === 'bySubmitTime'){
          model.sortOptions.bySubmitTime = model.sortOptions.bySubmitTime ? false : true;
      }
      if(field === 'byFinishTime'){
        model.sortOptions.byFinishTime = model.sortOptions.byFinishTime ? false : true;
    }
      if(field === 'bySubmitter'){
          model.sortOptions.bySubmitter = model.sortOptions.bySubmitter ? false : true;
      }
      if(field === 'byDirector'){
          model.sortOptions.byDirector = model.sortOptions.byDirector ? false : true;
      }
      if(field === 'byStatus'){
          model.sortOptions.byStatus = model.sortOptions.byStatus ? false : true;
      }

      model.applySort(); // 更新排序
  };

  const sortIcon = (field: keyof SortOptions) => {
    if (model.sortOptions[field] == null) return null;
    return model.sortOptions.order === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />;
  };

  return (
    <div className="filter-container">
        <Row className='time-range-row' gutter={[16, 16]}> 
            <Col>
                <IconFont type='icon-riqifanwei1' style={{ fontSize: 20, marginTop: '3px' }} />
            </Col>
            <Col>
                <Space size={10} className='date-pick'>
                    <Button
                        type="text"
                        className={timeFilterBtnClass('all')}
                        onClick={() => selectTimeFilter('all')}
                    >
                        全时期
                    </Button>
                    <span className="time-range-label">指定截止月</span>
                    <DatePicker
                        value={
                          model.monthFilterMode === 'month' && model.currentMonth
                            ? moment(model.currentMonth)
                            : undefined
                        }
                        picker="month"
                        onChange={ctrl.changeMonthRange}
                        className='date-picker' 
                        allowClear
                    />
                    <Button
                        type='text'
                        className={timeFilterBtnClass('month')}
                        onClick={() => {
                            selectTimeFilter(
                              'month',
                              DateUtil.getForeMonth(model.currentMonth ?? new Date()),
                            );
                        }}
                        >
                        上月
                    </Button>
                    <Button
                        type='text'
                        onClick={() => {
                            selectTimeFilter(
                              'month',
                              DateUtil.getNextMonth(model.currentMonth ?? new Date()),
                            );
                        }}
                        >
                        下月
                    </Button>
                    <Button
                        type='text'
                        className={timeFilterBtnClass('unscheduled')}
                        onClick={() => selectTimeFilter('unscheduled')}
                        >
                        未排期
                    </Button>
                </Space> 
            </Col>
            <Col flex="auto" className="add-project-col">
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} className="add-project-button">
                    新增项目
                </Button>
            </Col>
        </Row>
        <Row className="filter-row" gutter={[16, 16]}>
            <Col>
                <IconFont type='icon-shaixuan1' style={{fontSize:20,marginTop:'3px'}}/>
            </Col>
            <Col style={{paddingLeft:22}}>
                <Checkbox
                  checked={model.filterOptions.onlyIncomplete}
                  onChange={handleIncompleteChange}
                >
                  只看未完成
                </Checkbox>
            </Col>
            <Col>
                <Checkbox
                  checked={!model.filterOptions.onlyIncomplete && model.filterOptions.status.includes(0)}
                  onChange={() => handleFilterChange(0)}
                >
                  只看待开始
                </Checkbox>
            </Col>
            <Col>
                <Checkbox
                  checked={!model.filterOptions.onlyIncomplete && model.filterOptions.status.includes(1)}
                  onChange={() => handleFilterChange(1)}
                >
                  只看进行中
                </Checkbox>
            </Col>
            <Col>
                <Checkbox
                  checked={!model.filterOptions.onlyIncomplete && model.filterOptions.status.includes(2)}
                  onChange={() => handleFilterChange(2)}
                >
                  只看已完成
                </Checkbox>
            </Col>
            <Col>
                <Checkbox
                  checked={!model.filterOptions.onlyIncomplete && model.filterOptions.status.includes(3)}
                  onChange={() => handleFilterChange(3)}
                >
                  只看已废弃
                </Checkbox>
            </Col>
            <Col>
                <Checkbox onChange={handleOwnChange}>只看自己</Checkbox>
            </Col>
            
        </Row>
        <Row className="sort-row" gutter={[16, 16]}>
            <Col>
                <IconFont type='icon-icon-' style={{fontSize:20,marginTop:'3px'}}/>
            </Col>
            <Col className="sort-item">
            <Button type="text" onClick={() => handleSortChange('bySubmitTime')}>
                立项时间 {sortIcon('bySubmitTime')}
            </Button>
            </Col>
            <Col className="sort-item">
            <Button type="text" onClick={() => handleSortChange('bySubmitter')}>
                提出人 {sortIcon('bySubmitter')}
            </Button>
            </Col>
            <Col className="sort-item">
            <Button type="text" onClick={() => handleSortChange('byDirector')}>
                负责人 {sortIcon('byDirector')}
            </Button>
            </Col>
            <Col className="sort-item">
            <Button type="text" onClick={() => handleSortChange('byStatus')}>
                状态 {sortIcon('byStatus')}
            </Button>
            </Col>
            <Col flex="auto" className="sort-right">
            <Space size={0}>
                <span style={{marginRight: 20}}>共{model.filteredList.length}条</span>
                <Button  icon={<FolderOpenOutlined />} type="text" className='btn-refresh'  onClick={model.collapseAll}>折叠</Button>
                <Button icon={<ReloadOutlined />} type="text" className='btn-refresh' onClick={() => model.refresh()}>刷新</Button>
            </Space>
            </Col>
        </Row> 

        <AddEdit
            visible={isModalVisible}
            onClose={handleCloseModal} 
            currentUser={currentUser} 
            candidateDemands={projectPageModel.demandList}
        />
    </div>
  );
});

export default Filter;